import React, { useState, useEffect } from 'react';
import ClientesList from './ClientesList';
import ClienteModal from './ClienteModal/ClienteModal.js';
import './Clientes.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

// Helper to get auth header
const getAuthHeader = () => {
  return {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };
};

const ClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');

  const fetchClientes = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/clientes/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // If data is an object with a clients/data property, use that, otherwise use the data itself
      const clientesArray = data?.clientes || data?.data || data;
      
      // Ensure we're setting an array to the state
      const validClientes = Array.isArray(clientesArray) ? clientesArray : [];
      setClientes(validClientes);
      setFilteredClientes(validClientes);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar clientes: ' + err.message);
      console.error('Erro ao buscar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, []);

  // Filter clients based on active tab
  useEffect(() => {
    if (activeTab === 'todos') {
      setFilteredClientes(clientes);
    } else {
      // Filter logic here based on your categorization
      // This is just an example, you might need to adapt it to your data structure
      const filtered = clientes.filter(cliente => {
        if (activeTab === 'ativos' && cliente.status === 'ativo') return true;
        if (activeTab === 'inativos' && cliente.status === 'inativo') return true;
        return false;
      });
      setFilteredClientes(filtered);
    }
  }, [activeTab, clientes]);

  const handleClienteSelect = (cliente) => {
    setSelectedCliente(cliente);
    setShowForm(false);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setSelectedCliente(null);
    setIsEditing(false);
    setShowForm(true);
    setIsModalOpen(true);
  };

  const handleEdit = (cliente) => {
    setSelectedCliente(cliente);
    setIsEditing(true);
    setShowForm(true);
    setIsModalOpen(true);  // Make sure modal opens when editing
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/clientes/${id}`, {
          headers: getAuthHeader(),
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Refresh the client list
        fetchClientes();
        
        // Close details if the deleted client was selected
        if (selectedCliente && selectedCliente._id === id) {
          setSelectedCliente(null);
          setIsModalOpen(false);
        }
      } catch (err) {
        setError('Erro ao excluir cliente: ' + err.message);
        console.error('Erro ao excluir cliente:', err);
      }
    }
  };

  const handleFormSubmit = async (clienteData) => {
    try {      
      const url = isEditing
        ? `${API_BASE_URL}/api/clientes/${selectedCliente._id}`
        : `${API_BASE_URL}/api/clientes`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeader(),
        body: JSON.stringify(clienteData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const responseData = await response.json();

      // Refresh the client list
      fetchClientes();
      
      // Close the form
      setShowForm(false);
      setIsModalOpen(false);
      
      // If we just added a new client, clear the selection
      if (!isEditing) {
        setSelectedCliente(null);
      }
    } catch (err) {
      setError('Erro ao salvar cliente: ' + err.message);
      console.error('Erro ao salvar cliente:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    // If we're adding a new client and cancel, close the modal
    if (!isEditing) {
      setIsModalOpen(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <h1>Gerenciamento de Clientes</h1>
        <button className="clientes-btn clientes-btn-primary" onClick={handleAddNew}>
          Adicionar Cliente
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="clientes-card">
        <div className="tabs-header">
          <div 
            className={`tab-item ${activeTab === 'todos' ? 'active' : ''}`}
            onClick={() => handleTabChange('todos')}
          >
            Todos
          </div>
          <div 
            className={`tab-item ${activeTab === 'ativos' ? 'active' : ''}`}
            onClick={() => handleTabChange('ativos')}
          >
            Ativos
          </div>
          <div 
            className={`tab-item ${activeTab === 'inativos' ? 'active' : ''}`}
            onClick={() => handleTabChange('inativos')}
          >
            Inativos
          </div>
        </div>

        {loading ? (
          <div className="loading">Carregando clientes...</div>
        ) : (
          <ClientesList 
            clientes={filteredClientes} 
            onSelectCliente={handleClienteSelect}
            selectedId={selectedCliente?._id}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* Modal for client details or form */}
      <ClienteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        cliente={selectedCliente}
        showForm={showForm}
        isEditing={isEditing}
        onEdit={() => handleEdit(selectedCliente)}
        onDelete={() => {
          handleDelete(selectedCliente._id);
          setIsModalOpen(false);
        }}
        onFormSubmit={handleFormSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default ClientesPage;